import React, { useEffect } from "react";
import { useParams } from "react-router";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { ReactComponent as SearchIcon } from "assets/icons/search-white.svg";
import { Alert, Pagination } from "@material-ui/lab";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { SearchButton } from "components/searchButton";
import { Wrapper } from "components/wrapper";
import CustomBackdrop from "components/backdrop";
import { UltimaAtualizacao } from "components/ultimaAtualizacao";
import { ExportButtons } from "components/exportButtons";
import { StatCards } from "components/statCards";
import { SortDirectionSelect } from "components/sortDirectionSelect";
import { useDocumentosSetoriaisState } from "./context/documentos-setoriais-context";
import { useDocumentosSetoriaisController } from "./context/documentos-setoriais-controller";
import { DocumentosSetoriaisTable } from "./documentosSetoriaisTable.component";

export function DocumentosSetoriaisPage() {
  const { documentos, documentoSetorialPagedRequest, loading, tipos, areas, ultimaAtualizacao, totais, error } = useDocumentosSetoriaisState();
  const { onInit, handleOnChange, listDocumentosSetoriais, exportDocumentosSetoriais } = useDocumentosSetoriaisController();
  const { areaSlug } = useParams<{ areaSlug?: string }>();
  const areaSelecionada = areas?.find((a) => a.id === documentoSetorialPagedRequest?.idAreaTransparenciaSetorial);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      await onInit(areaSlug);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaSlug]);

  const handlePageClick = (event: React.ChangeEvent<unknown>, value: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      await listDocumentosSetoriais({ ...documentoSetorialPagedRequest, page: value - 1 }, true);
    })();
  };

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Saúde, Educação e Assistência Social", href: "/documentos-setoriais" },
          ...(areaSelecionada ? [{ label: areaSelecionada.name }] : []),
        ]}
      />
      <PageTitle>{areaSelecionada ? areaSelecionada.name : "Saúde, Educação e Assistência Social"}</PageTitle>
      <Wrapper>
        <StatCards
          items={[
            { label: "Total de Registros", value: String(totais?.total ?? 0), variant: 'blue' },
            { label: "Em Execução", value: String(totais?.emExecucao ?? 0) },
            { label: "Finalizados", value: String(totais?.finalizados ?? 0), variant: 'green' },
          ]}
        />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  variant="outlined"
                  fullWidth
                  name="text-field-term"
                  label="Descrição"
                  value={documentoSetorialPagedRequest?.term || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  variant="outlined"
                  fullWidth
                  name="text-field-ano"
                  label="Ano"
                  value={documentoSetorialPagedRequest?.ano || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Área</InputLabel>
                  <Select
                    name="select-area-id"
                    value={documentoSetorialPagedRequest?.idAreaTransparenciaSetorial || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Área"
                    displayEmpty
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {areas?.map((a) => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Tipo de Documento</InputLabel>
                  <Select
                    name="select-tipo-id"
                    value={documentoSetorialPagedRequest?.idTipoDocumentoSetorial || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Tipo de Documento"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {tipos?.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <SearchButton onClick={() => (async () => {
                  await listDocumentosSetoriais(documentoSetorialPagedRequest, true);
                })()}>
                  <SearchIcon /> Consultar
                </SearchButton>
              </Grid>
              <Grid item xs={12} sm={3}>
                <SortDirectionSelect
                  value={documentoSetorialPagedRequest?.orderDirection}
                  onChange={(e) => handleOnChange(e)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <CustomBackdrop open={loading} />
            <UltimaAtualizacao data={ultimaAtualizacao} />
            <ExportButtons onExport={exportDocumentosSetoriais} />
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <DocumentosSetoriaisTable currentItems={documentos} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3} justifyContent="center">
              <Pagination
                size="large"
                variant="outlined"
                shape="rounded"
                onChange={handlePageClick}
                count={documentos?.pageCount}
                color="primary"
              />
            </Grid>
          </Grid>
        </Grid>
      </Wrapper>
    </>
  );
}
